// ── JobSpec ──
// First-class execution spec bound to a Job at creation. Source of truth for
// what gets installed, what gets photographed, and what gets QA'd.
//
// Design constraints (locked):
//  • scopeOfWork.ts is LEGACY — JobSpec is authoritative going forward.
//  • BOM is owned here directly (no buildConfigMapping integration yet).
//  • Versioning is simple integers (v1, v2, v3) — no hashing/snapshot logic.
//  • Archetype values MUST align with SpecArchetype (Pay Profile enum).
//  • Once bound to a job, the spec/version is immutable.

import type { SpecArchetype } from "./contractorPayProfile";
export type { SpecArchetype } from "./contractorPayProfile";

export type JobSpecStatus = "canonical" | "experimental";

export type JobSpecPhaseName = "Arrival, Prep & Protection" | "Demolition & Structural Assessment" | "Framing, Plumbing & Rough-In" | "Pan, Substrate & Wall Installation" | "Trim, Fixtures & Waterproof Finishing" | "Final QA, Water Testing & Closeout";

export type RequiredPhotoType =
  // Prep
  | "prep_floor_protection"
  | "prep_water_shutoff"
  | "prep_workspace_staged"
  // Demo / before
  | "before_existing_tub"
  | "before_walls"
  | "before_plumbing_access"
  | "demo_complete_substrate"
  | "demo_debris_removed"
  // Plumbing
  | "rough_plumbing_valve"
  | "rough_plumbing_drain"
  | "pressure_test"
  // Install
  | "pan_set"
  | "wall_panels_installed"
  | "glass_door_installed"
  | "trim_complete"
  // QA / final
  | "final_full_shower"
  | "final_caulk_lines"
  | "final_drain_function"
  | "site_cleaned"
  // Final QA / Closeout
  | "qa_final_shower_overview"
  | "qa_final_pan_basin"
  | "qa_final_fixtures_accessories"
  | "qa_final_seat_installation"
  | "qa_water_test_verification"
  | "qa_site_cleaned_complete";

/**
 * Full pool of RequiredPhotoType values, ordered by phase. Used by the
 * JobSpec authoring panel so editors can toggle which required photos
 * apply to a phase. (Adding new photo *types* is intentionally out of
 * scope — the authoring layer can only choose from the canonical pool.)
 */
export const ALL_REQUIRED_PHOTO_TYPES: RequiredPhotoType[] = [
  "prep_floor_protection",
  "prep_water_shutoff",
  "prep_workspace_staged",
  "before_existing_tub",
  "before_walls",
  "before_plumbing_access",
  "demo_complete_substrate",
  "demo_debris_removed",
  "rough_plumbing_valve",
  "rough_plumbing_drain",
  "pressure_test",
  "pan_set",
  "wall_panels_installed",
  "glass_door_installed",
  "trim_complete",
  "final_full_shower",
  "final_caulk_lines",
  "final_drain_function",
  "site_cleaned",
  "qa_final_shower_overview",
  "qa_final_pan_basin",
  "qa_final_fixtures_accessories",
  "qa_final_seat_installation",
  "qa_water_test_verification",
  "qa_site_cleaned_complete",
];

/**
 * Which team is expected to execute a given phase. Surfaced in the UI so other
 * roles (Dispatch, PM, CX) can see at a glance who owns the work — even when
 * Prep is handled by a separate crew ahead of the install team. Pure UI hint;
 * does not change execution logic, gating, or routing.
 */
export type ResponsibleTeam = "Install Crew" | "Prep Crew" | "Plumbing Sub";

export interface JobSpecChecklistItem {
  id: string;
  label: string;
}

export interface JobSpecPhase {
  name: JobSpecPhaseName;
  label: string;
  requiredPhotos: RequiredPhotoType[];
  checklistItems: JobSpecChecklistItem[];
  /** Optional ownership hint (UI only). Defaults to "Install Crew" when absent. */
  responsibleTeam?: ResponsibleTeam;
}

export interface JobSpecBomItem {
  sku: string;
  name: string;
  quantity: number;
  unit?: string;
  cost?: number;   // optional — unit cost from product registry
  price?: number;  // optional — list price from product registry
}

export interface JobSpec {
  /** Stable identifier, e.g. "TTS-STD-60x32" */
  specId: string;
  /** Integer version only. v1, v2, v3 — no semver, no hashes. */
  versionNumber: number;
  /** Human label, e.g. "Tub-to-Shower Standard 60x32" */
  label: string;
  archetype: SpecArchetype;
  status: JobSpecStatus;
  phases: JobSpecPhase[];
  billOfMaterials: JobSpecBomItem[];
}

/**
 * Frozen snapshot of execution-critical spec fields, captured at bind time.
 * This is the *truth* the job runs against — registry edits to the canonical
 * spec must NEVER mutate this. `specId` + `versionNumber` are labels for
 * analytics; `snapshot` is the lock.
 */
export interface BoundSpecSnapshot {
  /** Display label captured at bind time (registry label may drift). */
  label: string;
  archetype: SpecArchetype;
  /** Status at bind time — "canonical" | "experimental". */
  status: JobSpecStatus;
  phases: JobSpecPhase[];
  billOfMaterials: JobSpecBomItem[];
}

/** Snapshot reference attached to a Job. Immutable after binding. */
export interface BoundJobSpec {
  specId: string;
  versionNumber: number;
  archetype: SpecArchetype;
  /** ISO timestamp — when spec was attached to job */
  boundAt: string;
  /** ISO timestamp — when spec was sale-stage locked. Reserved; unused for now. */
  lockedAt?: string;
  /**
   * Frozen snapshot of execution-critical spec fields. Mandatory for new
   * bindings. May be absent on legacy jobs bound before pinning was added —
   * in that case `resolveBoundSpec` returns undefined and
   * `isLegacyBinding(bound)` returns true.
   */
  snapshot?: BoundSpecSnapshot;
}

// ── Registry ──────────────────────────────────────────────────────────────
// Only one canonical version per archetype. New canonical replaces old.

export const JOB_SPECS: JobSpec[] = [
  {
    specId: "TTS-STD-60x32",
    versionNumber: 1,
    label: "Tub-to-Shower Standard 60×32",
    archetype: "TTS-STD",
    status: "canonical",
    phases: [
      {
        name: "Arrival, Prep & Protection",
        label: "Arrival, Prep & Protection",
        responsibleTeam: "Install Crew",
        requiredPhotos: [
          "prep_floor_protection",
          "prep_water_shutoff",
          "prep_workspace_staged",
        ],
        checklistItems: [
          { id: "prep-1", label: "Floor protection laid from entry to bath" },
          { id: "prep-2", label: "Main water supply shut off and verified" },
          { id: "prep-3", label: "Fixtures masked, vents covered" },
          { id: "prep-4", label: "Tools and materials staged in workspace" },
          { id: "prep-5", label: "Customer briefed on dust/noise window" },
        ],
      },
      {
        name: "Demolition & Structural Assessment",
        label: "Demolition & Structural Assessment",
        responsibleTeam: "Install Crew",
        requiredPhotos: [
          "before_existing_tub",
          "before_walls",
          "before_plumbing_access",
          "demo_complete_substrate",
          "demo_debris_removed",
        ],
        checklistItems: [
          { id: "demo-1", label: "Existing tub & surround removed" },
          { id: "demo-2", label: "Substrate inspected for rot/mold" },
          { id: "demo-3", label: "Floor framing verified level" },
          { id: "demo-4", label: "Debris bagged and staged for haul-off" },
        ],
      },
      {
        name: "Framing, Plumbing & Rough-In",
        label: "Framing, Plumbing & Rough-In",
        responsibleTeam: "Install Crew",
        requiredPhotos: [
          "rough_plumbing_valve",
          "rough_plumbing_drain",
          "pressure_test",
        ],
        checklistItems: [
          { id: "plb-1", label: "Mixing valve installed to spec depth" },
          { id: "plb-2", label: "Drain relocated/aligned to pan outlet" },
          { id: "plb-3", label: "Pressure test held 15 min, no drop" },
          { id: "plb-4", label: "Shut-offs accessible & labeled" },
        ],
      },
      {
        name: "Pan, Substrate & Wall Installation",
        label: "Pan, Substrate & Wall Installation",
        requiredPhotos: [
          "pan_set",
          "wall_panels_installed",
          "glass_door_installed",
          "trim_complete",
        ],
        checklistItems: [
          { id: "ins-1", label: "Shower pan set level (≤1/8\" over span)" },
          { id: "ins-2", label: "Wall panels seamed & sealed per manufacturer" },
          { id: "ins-3", label: "Glass door plumb and operating smoothly" },
          { id: "ins-4", label: "Trim, niche, and accessories installed" },
          { id: "ins-5", label: "All caulk joints tooled cleanly" },
        ],
      },
      {
        name: "Trim, Fixtures & Waterproof Finishing",
        label: "Trim, Fixtures & Waterproof Finishing",
        requiredPhotos: [
          "final_full_shower",
          "final_caulk_lines",
          "final_drain_function",
          "site_cleaned",
        ],
        checklistItems: [
          { id: "qa-1", label: "Water test — no leaks at valve, drain, seams" },
          { id: "qa-2", label: "Caulk lines uniform, no gaps or smears" },
          { id: "qa-3", label: "Door swings, latches, and seals correctly" },
          { id: "qa-4", label: "Site cleaned, customer walkthrough complete" },
          { id: "qa-5", label: "Customer signature captured" },
        ],
      },
      {
        name: "Final QA, Water Testing & Closeout",
        label: "Final QA, Water Testing & Closeout",
        requiredPhotos: [
          "qa_final_shower_overview",
          "qa_final_pan_basin",
          "qa_final_fixtures_accessories",
          "qa_final_seat_installation",
          "qa_water_test_verification",
          "qa_site_cleaned_complete",
        ],
        checklistItems: [
          { id: "final-1", label: "Perform final water test and leak inspection" },
          { id: "final-2", label: "Verify drain flow and proper water evacuation" },
          { id: "final-3", label: "Verify shower valve operation and temperature control" },
          { id: "final-4", label: "Confirm all fixtures function correctly" },
          { id: "final-5", label: "Verify shower seat operation and stability" },
          { id: "final-6", label: "Inspect all sealant lines and waterproofed joints" },
          { id: "final-7", label: "Complete final cleaning of work area" },
          { id: "final-8", label: "Remove all debris and materials from site" },
          { id: "final-9", label: "Conduct customer walkthrough" },
          { id: "final-10", label: "Document punch-list items or exceptions if applicable" },
          { id: "final-11", label: "Obtain customer sign-off" },
          { id: "final-12", label: "Submit installation package for QA review" },
        ],
      },
    ],
    billOfMaterials: [
      { sku: "PAN-60x32-WHT", name: "Shower Pan 60×32 White", quantity: 1, unit: "ea" },
      { sku: "WALL-KIT-3PC-60x32", name: "3-Piece Wall Surround 60×32", quantity: 1, unit: "kit" },
      { sku: "VALVE-PB-1H", name: "Pressure-Balance Mixing Valve, 1-Handle", quantity: 1, unit: "ea" },
      { sku: "TRIM-CHR-RND", name: "Valve Trim — Chrome, Round", quantity: 1, unit: "ea" },
      { sku: "SHWR-HEAD-CHR", name: "Shower Head — Chrome", quantity: 1, unit: "ea" },
      { sku: "DRN-ABS-2IN", name: "Shower Drain Assembly 2\"", quantity: 1, unit: "ea" },
      { sku: "DOOR-GL-60-CHR", name: "Sliding Glass Door 60\" — Chrome", quantity: 1, unit: "ea" },
      { sku: "NICHE-12x24", name: "Recessed Niche 12×24", quantity: 1, unit: "ea" },
      { sku: "CAULK-100SIL-WHT", name: "100% Silicone Caulk — White", quantity: 3, unit: "tube" },
      { sku: "CEMBOARD-3x5", name: "Cement Backer Board 3×5×½\"", quantity: 4, unit: "sheet" },
      { sku: "SCREW-CB-1.25", name: "Cement Board Screws 1¼\"", quantity: 1, unit: "box" },
      { sku: "MEMBRANE-WP", name: "Waterproof Membrane Roll", quantity: 1, unit: "roll" },
    ],
  },

  // ── SHWR-RPL: Shower Replacement / Remodel ──────────────────────────────
  // For existing-shower retrofit (no tub demo). Smaller demo, same plumbing
  // and install/QA discipline as TTS.
  {
    specId: "SHWR-RPL-STD-60x32",
    versionNumber: 1,
    label: "Shower Replacement Standard 60×32",
    archetype: "SHWR-RPL",
    status: "canonical",
    phases: [
      {
        name: "Arrival, Prep & Protection",
        label: "Arrival, Prep & Protection",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["prep_floor_protection", "prep_water_shutoff", "prep_workspace_staged"],
        checklistItems: [
          { id: "prep-1", label: "Floor protection laid from entry to bath" },
          { id: "prep-2", label: "Main water supply shut off and verified" },
          { id: "prep-3", label: "Fixtures masked, vents covered" },
          { id: "prep-4", label: "Tools and materials staged in workspace" },
        ],
      },
      {
        name: "Demolition & Structural Assessment",
        label: "Demolition & Structural Assessment",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["before_existing_tub", "before_walls", "before_plumbing_access", "demo_complete_substrate", "demo_debris_removed"],
        checklistItems: [
          { id: "demo-1", label: "Existing shower pan & surround removed" },
          { id: "demo-2", label: "Substrate inspected for rot/mold" },
          { id: "demo-3", label: "Drain location verified vs new pan outlet" },
          { id: "demo-4", label: "Debris bagged and staged for haul-off" },
        ],
      },
      {
        name: "Framing, Plumbing & Rough-In",
        label: "Framing, Plumbing & Rough-In",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["rough_plumbing_valve", "rough_plumbing_drain", "pressure_test"],
        checklistItems: [
          { id: "plb-1", label: "Mixing valve installed to spec depth" },
          { id: "plb-2", label: "Drain aligned to pan outlet" },
          { id: "plb-3", label: "Pressure test held 15 min, no drop" },
        ],
      },
      {
        name: "Pan, Substrate & Wall Installation",
        label: "Pan, Substrate & Wall Installation",
        requiredPhotos: ["pan_set", "wall_panels_installed", "glass_door_installed", "trim_complete"],
        checklistItems: [
          { id: "ins-1", label: "Replacement pan set level (≤1/8\" over span)" },
          { id: "ins-2", label: "Wall panels seamed & sealed per manufacturer" },
          { id: "ins-3", label: "Glass door plumb and operating smoothly" },
          { id: "ins-4", label: "Trim and accessories installed" },
          { id: "ins-5", label: "All caulk joints tooled cleanly" },
        ],
      },
      {
        name: "Trim, Fixtures & Waterproof Finishing",
        label: "Trim, Fixtures & Waterproof Finishing",
        requiredPhotos: ["final_full_shower", "final_caulk_lines", "final_drain_function", "site_cleaned"],
        checklistItems: [
          { id: "qa-1", label: "Water test — no leaks at valve, drain, seams" },
          { id: "qa-2", label: "Caulk lines uniform, no gaps or smears" },
          { id: "qa-3", label: "Door swings, latches, and seals correctly" },
          { id: "qa-4", label: "Site cleaned, customer walkthrough complete" },
          { id: "qa-5", label: "Customer signature captured" },
        ],
      },
      {
        name: "Final QA, Water Testing & Closeout",
        label: "Final QA, Water Testing & Closeout",
        requiredPhotos: [
          "qa_final_shower_overview",
          "qa_final_pan_basin",
          "qa_final_fixtures_accessories",
          "qa_final_seat_installation",
          "qa_water_test_verification",
          "qa_site_cleaned_complete",
        ],
        checklistItems: [
          { id: "final-1", label: "Perform final water test and leak inspection" },
          { id: "final-2", label: "Verify drain flow and proper water evacuation" },
          { id: "final-3", label: "Verify shower valve operation and temperature control" },
          { id: "final-4", label: "Confirm all fixtures function correctly" },
          { id: "final-5", label: "Verify shower seat operation and stability" },
          { id: "final-6", label: "Inspect all sealant lines and waterproofed joints" },
          { id: "final-7", label: "Complete final cleaning of work area" },
          { id: "final-8", label: "Remove all debris and materials from site" },
          { id: "final-9", label: "Conduct customer walkthrough" },
          { id: "final-10", label: "Document punch-list items or exceptions if applicable" },
          { id: "final-11", label: "Obtain customer sign-off" },
          { id: "final-12", label: "Submit installation package for QA review" },
        ],
      },
    ],
    billOfMaterials: [
      { sku: "PAN-60x32-WHT", name: "Shower Pan 60×32 White", quantity: 1, unit: "ea" },
      { sku: "WALL-KIT-3PC-60x32", name: "3-Piece Wall Surround 60×32", quantity: 1, unit: "kit" },
      { sku: "VALVE-PB-1H", name: "Pressure-Balance Mixing Valve, 1-Handle", quantity: 1, unit: "ea" },
      { sku: "TRIM-CHR-RND", name: "Valve Trim — Chrome, Round", quantity: 1, unit: "ea" },
      { sku: "SHWR-HEAD-CHR", name: "Shower Head — Chrome", quantity: 1, unit: "ea" },
      { sku: "DRN-ABS-2IN", name: "Shower Drain Assembly 2\"", quantity: 1, unit: "ea" },
      { sku: "DOOR-GL-60-CHR", name: "Sliding Glass Door 60\" — Chrome", quantity: 1, unit: "ea" },
      { sku: "CAULK-100SIL-WHT", name: "100% Silicone Caulk — White", quantity: 3, unit: "tube" },
      { sku: "CEMBOARD-3x5", name: "Cement Backer Board 3×5×½\"", quantity: 3, unit: "sheet" },
      { sku: "SCREW-CB-1.25", name: "Cement Board Screws 1¼\"", quantity: 1, unit: "box" },
      { sku: "MEMBRANE-WP", name: "Waterproof Membrane Roll", quantity: 1, unit: "roll" },
    ],
  },

  // ── WALK-IN-TUB: Walk-in Tub Install ────────────────────────────────────
  {
    specId: "WALK-IN-TUB-STD",
    versionNumber: 1,
    label: "Walk-in Tub Standard",
    archetype: "WALK-IN-TUB",
    status: "canonical",
    phases: [
      {
        name: "Arrival, Prep & Protection",
        label: "Arrival, Prep & Protection",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["prep_floor_protection", "prep_water_shutoff", "prep_workspace_staged"],
        checklistItems: [
          { id: "prep-1", label: "Floor protection laid from entry to bath" },
          { id: "prep-2", label: "Water supply shut off and verified" },
          { id: "prep-3", label: "Door swing clearance verified for unit delivery" },
          { id: "prep-4", label: "Tools and materials staged in workspace" },
        ],
      },
      {
        name: "Demolition & Structural Assessment",
        label: "Demolition & Structural Assessment",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["before_existing_tub", "before_walls", "before_plumbing_access", "demo_complete_substrate", "demo_debris_removed"],
        checklistItems: [
          { id: "demo-1", label: "Existing tub & surround removed" },
          { id: "demo-2", label: "Subfloor inspected and reinforced if needed" },
          { id: "demo-3", label: "Electrical for jets routed (if hydrotherapy)" },
          { id: "demo-4", label: "Debris bagged and staged for haul-off" },
        ],
      },
      {
        name: "Framing, Plumbing & Rough-In",
        label: "Framing, Plumbing & Rough-In",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["rough_plumbing_valve", "rough_plumbing_drain", "pressure_test"],
        checklistItems: [
          { id: "plb-1", label: "Hot/cold supply roughed to walk-in valve" },
          { id: "plb-2", label: "2\" drain with overflow tied in" },
          { id: "plb-3", label: "Pressure test held 15 min, no drop" },
          { id: "plb-4", label: "Heater/jet circuit tied off (if applicable)" },
        ],
      },
      {
        name: "Pan, Substrate & Wall Installation",
        label: "Pan, Substrate & Wall Installation",
        requiredPhotos: ["pan_set", "wall_panels_installed", "trim_complete"],
        checklistItems: [
          { id: "ins-1", label: "Walk-in tub leveled and shimmed; door seals true" },
          { id: "ins-2", label: "Surround panels installed and sealed" },
          { id: "ins-3", label: "Door gasket inspected and door cycled 5×" },
          { id: "ins-4", label: "Grab bars installed to spec height/anchor" },
          { id: "ins-5", label: "All caulk joints tooled cleanly" },
        ],
      },
      {
        name: "Trim, Fixtures & Waterproof Finishing",
        label: "Trim, Fixtures & Waterproof Finishing",
        requiredPhotos: ["final_full_shower", "final_caulk_lines", "final_drain_function", "site_cleaned"],
        checklistItems: [
          { id: "qa-1", label: "Fill/drain test — door seal holds, no leaks" },
          { id: "qa-2", label: "Caulk lines uniform, no gaps or smears" },
          { id: "qa-3", label: "Customer walked through door operation & controls" },
          { id: "qa-4", label: "Site cleaned, customer walkthrough complete" },
          { id: "qa-5", label: "Customer signature captured" },
        ],
      },
      {
        name: "Final QA, Water Testing & Closeout",
        label: "Final QA, Water Testing & Closeout",
        requiredPhotos: [
          "qa_final_shower_overview",
          "qa_final_pan_basin",
          "qa_final_fixtures_accessories",
          "qa_final_seat_installation",
          "qa_water_test_verification",
          "qa_site_cleaned_complete",
        ],
        checklistItems: [
          { id: "final-1", label: "Perform final water test and leak inspection" },
          { id: "final-2", label: "Verify drain flow and proper water evacuation" },
          { id: "final-3", label: "Verify shower valve operation and temperature control" },
          { id: "final-4", label: "Confirm all fixtures function correctly" },
          { id: "final-5", label: "Verify shower seat operation and stability" },
          { id: "final-6", label: "Inspect all sealant lines and waterproofed joints" },
          { id: "final-7", label: "Complete final cleaning of work area" },
          { id: "final-8", label: "Remove all debris and materials from site" },
          { id: "final-9", label: "Conduct customer walkthrough" },
          { id: "final-10", label: "Document punch-list items or exceptions if applicable" },
          { id: "final-11", label: "Obtain customer sign-off" },
          { id: "final-12", label: "Submit installation package for QA review" },
        ],
      },
    ],
    billOfMaterials: [
      { sku: "WIT-UNIT-STD", name: "Walk-in Tub Unit, Standard", quantity: 1, unit: "ea" },
      { sku: "WIT-SURROUND-3PC", name: "Walk-in Tub Surround, 3-Piece", quantity: 1, unit: "kit" },
      { sku: "VALVE-PB-1H", name: "Pressure-Balance Mixing Valve, 1-Handle", quantity: 1, unit: "ea" },
      { sku: "TRIM-CHR-RND", name: "Valve Trim — Chrome, Round", quantity: 1, unit: "ea" },
      { sku: "TUB-FILL-CHR", name: "Tub Filler Spout — Chrome", quantity: 1, unit: "ea" },
      { sku: "DRN-OVF-2IN", name: "Drain + Overflow Assembly 2\"", quantity: 1, unit: "ea" },
      { sku: "GRAB-BAR-24-CHR", name: "Grab Bar 24\" Chrome", quantity: 2, unit: "ea" },
      { sku: "CAULK-100SIL-WHT", name: "100% Silicone Caulk — White", quantity: 3, unit: "tube" },
      { sku: "CEMBOARD-3x5", name: "Cement Backer Board 3×5×½\"", quantity: 3, unit: "sheet" },
      { sku: "SCREW-CB-1.25", name: "Cement Board Screws 1¼\"", quantity: 1, unit: "box" },
      { sku: "MEMBRANE-WP", name: "Waterproof Membrane Roll", quantity: 1, unit: "roll" },
    ],
  },

  // ── FULL-BATH: Full Bathroom Remodel ────────────────────────────────────
  {
    specId: "FULL-BATH-STD",
    versionNumber: 1,
    label: "Full Bathroom Remodel — Standard",
    archetype: "FULL-BATH",
    status: "canonical",
    phases: [
      {
        name: "Arrival, Prep & Protection",
        label: "Arrival, Prep & Protection",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["prep_floor_protection", "prep_water_shutoff", "prep_workspace_staged"],
        checklistItems: [
          { id: "prep-1", label: "Floor protection laid from entry through hall" },
          { id: "prep-2", label: "Main water supply shut off and verified" },
          { id: "prep-3", label: "Vanity / toilet / fixtures masked or removed" },
          { id: "prep-4", label: "Tools and materials staged in workspace" },
          { id: "prep-5", label: "Customer briefed on dust/noise window & timeline" },
        ],
      },
      {
        name: "Demolition & Structural Assessment",
        label: "Demolition & Structural Assessment",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["before_existing_tub", "before_walls", "before_plumbing_access", "demo_complete_substrate", "demo_debris_removed"],
        checklistItems: [
          { id: "demo-1", label: "Tub/shower, vanity, toilet removed" },
          { id: "demo-2", label: "Floor & wall substrate inspected for rot/mold" },
          { id: "demo-3", label: "Plumbing rough-in exposed and verified to plan" },
          { id: "demo-4", label: "Debris bagged and staged for haul-off" },
        ],
      },
      {
        name: "Framing, Plumbing & Rough-In",
        label: "Framing, Plumbing & Rough-In",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["rough_plumbing_valve", "rough_plumbing_drain", "pressure_test"],
        checklistItems: [
          { id: "plb-1", label: "Shower valve installed to spec depth" },
          { id: "plb-2", label: "Vanity & toilet supply / drain tied in" },
          { id: "plb-3", label: "Pressure test held 15 min, no drop" },
          { id: "plb-4", label: "Shut-offs accessible & labeled at every fixture" },
        ],
      },
      {
        name: "Pan, Substrate & Wall Installation",
        label: "Pan, Substrate & Wall Installation",
        requiredPhotos: ["pan_set", "wall_panels_installed", "glass_door_installed", "trim_complete"],
        checklistItems: [
          { id: "ins-1", label: "Shower pan set level (≤1/8\" over span)" },
          { id: "ins-2", label: "Wall panels / tile seamed & sealed per manufacturer" },
          { id: "ins-3", label: "Vanity set level, plumbed, and secured" },
          { id: "ins-4", label: "Toilet set with new wax ring; flange height verified" },
          { id: "ins-5", label: "Glass door plumb and operating smoothly" },
          { id: "ins-6", label: "All caulk joints tooled cleanly" },
        ],
      },
      {
        name: "Trim, Fixtures & Waterproof Finishing",
        label: "Trim, Fixtures & Waterproof Finishing",
        requiredPhotos: ["final_full_shower", "final_caulk_lines", "final_drain_function", "site_cleaned"],
        checklistItems: [
          { id: "qa-1", label: "Water test — no leaks at any fixture or seam" },
          { id: "qa-2", label: "Caulk lines uniform, no gaps or smears" },
          { id: "qa-3", label: "Toilet flushes / refills cleanly; no rocking" },
          { id: "qa-4", label: "Vanity drawers/doors operate smoothly" },
          { id: "qa-5", label: "Site cleaned, customer walkthrough complete" },
          { id: "qa-6", label: "Customer signature captured" },
        ],
      },
      {
        name: "Final QA, Water Testing & Closeout",
        label: "Final QA, Water Testing & Closeout",
        requiredPhotos: [
          "qa_final_shower_overview",
          "qa_final_pan_basin",
          "qa_final_fixtures_accessories",
          "qa_final_seat_installation",
          "qa_water_test_verification",
          "qa_site_cleaned_complete",
        ],
        checklistItems: [
          { id: "final-1", label: "Perform final water test and leak inspection" },
          { id: "final-2", label: "Verify drain flow and proper water evacuation" },
          { id: "final-3", label: "Verify shower valve operation and temperature control" },
          { id: "final-4", label: "Confirm all fixtures function correctly" },
          { id: "final-5", label: "Verify shower seat operation and stability" },
          { id: "final-6", label: "Inspect all sealant lines and waterproofed joints" },
          { id: "final-7", label: "Complete final cleaning of work area" },
          { id: "final-8", label: "Remove all debris and materials from site" },
          { id: "final-9", label: "Conduct customer walkthrough" },
          { id: "final-10", label: "Document punch-list items or exceptions if applicable" },
          { id: "final-11", label: "Obtain customer sign-off" },
          { id: "final-12", label: "Submit installation package for QA review" },
        ],
      },
    ],
    billOfMaterials: [
      { sku: "PAN-60x32-WHT", name: "Shower Pan 60×32 White", quantity: 1, unit: "ea" },
      { sku: "WALL-KIT-3PC-60x32", name: "3-Piece Wall Surround 60×32", quantity: 1, unit: "kit" },
      { sku: "VALVE-PB-1H", name: "Pressure-Balance Mixing Valve, 1-Handle", quantity: 1, unit: "ea" },
      { sku: "TRIM-CHR-RND", name: "Valve Trim — Chrome, Round", quantity: 1, unit: "ea" },
      { sku: "SHWR-HEAD-CHR", name: "Shower Head — Chrome", quantity: 1, unit: "ea" },
      { sku: "DRN-ABS-2IN", name: "Shower Drain Assembly 2\"", quantity: 1, unit: "ea" },
      { sku: "DOOR-GL-60-CHR", name: "Sliding Glass Door 60\" — Chrome", quantity: 1, unit: "ea" },
      { sku: "VANITY-36-WHT", name: "Vanity 36\" White w/ Top", quantity: 1, unit: "ea" },
      { sku: "VAN-FAUCET-CHR", name: "Vanity Faucet — Chrome, Single-Hole", quantity: 1, unit: "ea" },
      { sku: "TOILET-RH-WHT", name: "Toilet — Right-Height, Elongated, White", quantity: 1, unit: "ea" },
      { sku: "WAX-RING-STD", name: "Wax Ring + Flange Bolts", quantity: 1, unit: "kit" },
      { sku: "MIRROR-30x36", name: "Vanity Mirror 30×36", quantity: 1, unit: "ea" },
      { sku: "CAULK-100SIL-WHT", name: "100% Silicone Caulk — White", quantity: 4, unit: "tube" },
      { sku: "CEMBOARD-3x5", name: "Cement Backer Board 3×5×½\"", quantity: 4, unit: "sheet" },
      { sku: "SCREW-CB-1.25", name: "Cement Board Screws 1¼\"", quantity: 1, unit: "box" },
      { sku: "MEMBRANE-WP", name: "Waterproof Membrane Roll", quantity: 1, unit: "roll" },
    ],
  },

  // ── WINDOW: Window Install ──────────────────────────────────────────────
  // Distinct lifecycle from wet-trades: no plumbing phase. Phases reused
  // from the shared enum (prep / demo / install / qa) — no plumbing entry.
  {
    specId: "WINDOW-STD",
    versionNumber: 1,
    label: "Window Install — Standard",
    archetype: "WINDOW",
    status: "canonical",
    phases: [
      {
        name: "Arrival, Prep & Protection",
        label: "Arrival, Prep & Protection",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["prep_floor_protection", "prep_workspace_staged"],
        checklistItems: [
          { id: "prep-1", label: "Interior floor & furniture protected at every opening" },
          { id: "prep-2", label: "Exterior staging clear; ladders set safely" },
          { id: "prep-3", label: "Each opening measured and re-verified vs unit" },
          { id: "prep-4", label: "Tools and materials staged at each opening" },
        ],
      },
      {
        name: "Demolition & Structural Assessment",
        label: "Demolition & Structural Assessment",
        responsibleTeam: "Install Crew",
        requiredPhotos: ["before_walls", "demo_complete_substrate", "demo_debris_removed"],
        checklistItems: [
          { id: "demo-1", label: "Existing windows removed without frame damage" },
          { id: "demo-2", label: "Rough opening inspected for rot / out-of-square" },
          { id: "demo-3", label: "Sill flashed and prepped for new unit" },
          { id: "demo-4", label: "Debris bagged and staged for haul-off" },
        ],
      },
      {
        name: "Pan, Substrate & Wall Installation",
        label: "Pan, Substrate & Wall Installation",
        requiredPhotos: ["wall_panels_installed", "trim_complete"],
        checklistItems: [
          { id: "ins-1", label: "Each unit set plumb, level, and square (±1/8\")" },
          { id: "ins-2", label: "Shimmed and fastened per manufacturer schedule" },
          { id: "ins-3", label: "Exterior flashing tape lapped correctly (shingle)" },
          { id: "ins-4", label: "Interior trim and casing reinstalled / replaced" },
          { id: "ins-5", label: "Caulk + foam seal applied; no gaps to weather" },
        ],
      },
      {
        name: "Trim, Fixtures & Waterproof Finishing",
        label: "Trim, Fixtures & Waterproof Finishing",
        requiredPhotos: ["final_caulk_lines", "site_cleaned"],
        checklistItems: [
          { id: "qa-1", label: "Each window opens, locks, and tilts as designed" },
          { id: "qa-2", label: "Caulk lines uniform inside & out, no gaps" },
          { id: "qa-3", label: "Screens installed and verified" },
          { id: "qa-4", label: "Site cleaned (interior + exterior), nail sweep done" },
          { id: "qa-5", label: "Customer walkthrough complete; signature captured" },
        ],
      },
    ],
    billOfMaterials: [
      { sku: "WIN-DH-36x60-WHT", name: "Double-Hung Window 36×60 White", quantity: 1, unit: "ea" },
      { sku: "WIN-SCREEN-36x60", name: "Window Screen 36×60", quantity: 1, unit: "ea" },
      { sku: "FLASH-TAPE-4IN", name: "Flashing Tape 4\" × 75'", quantity: 1, unit: "roll" },
      { sku: "FOAM-WIN-DOOR", name: "Low-Expansion Window/Door Foam", quantity: 2, unit: "can" },
      { sku: "SHIM-CDR-PK", name: "Cedar Shim Pack", quantity: 1, unit: "pk" },
      { sku: "CAULK-100SIL-WHT", name: "100% Silicone Caulk — White", quantity: 2, unit: "tube" },
      { sku: "TRIM-CASING-PVC", name: "Window Casing PVC, 8'", quantity: 4, unit: "ea" },
      { sku: "SCREW-EXT-2.5", name: "Exterior Screws 2½\"", quantity: 1, unit: "box" },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────

export function getJobSpec(specId: string, versionNumber: number): JobSpec | undefined {
  return JOB_SPECS.find(s => s.specId === specId && s.versionNumber === versionNumber);
}

export function getCanonicalSpecForArchetype(archetype: SpecArchetype): JobSpec | undefined {
  return JOB_SPECS.find(s => s.archetype === archetype && s.status === "canonical");
}

/** Latest canonical version number for a given specId, if one exists. */
export function getLatestCanonicalVersion(specId: string): number | undefined {
  const matches = JOB_SPECS.filter(s => s.specId === specId && s.status === "canonical");
  if (matches.length === 0) return undefined;
  return Math.max(...matches.map(s => s.versionNumber));
}

/**
 * SINGLE READ PATH for execution/QA/material flows.
 *
 * Returns a JobSpec-shaped view of the bound spec. Always reads from the
 * frozen `snapshot` when present — never from the live registry. This is the
 * enforcement mechanism for version pinning: future edits to the canonical
 * spec cannot mutate jobs that were already bound.
 *
 * Legacy bindings without a snapshot intentionally do not resolve. Falling
 * back to the live registry can surface a generic canonical BOM for a job
 * that never had real material lines, which makes imported/legacy jobs look
 * like they have generated materials.
 */
export function resolveBoundSpec(bound?: BoundJobSpec): JobSpec | undefined {
  if (!bound) return undefined;
  if (bound.snapshot) {
    return {
      specId: bound.specId,
      versionNumber: bound.versionNumber,
      label: bound.snapshot.label,
      archetype: bound.snapshot.archetype,
      status: bound.snapshot.status,
      phases: bound.snapshot.phases,
      billOfMaterials: bound.snapshot.billOfMaterials,
    };
  }
  return undefined;
}

/** True for jobs bound before pinning was introduced (no snapshot). */
export function isLegacyBinding(bound?: BoundJobSpec): boolean {
  return !!bound && !bound.snapshot;
}

/**
 * Bind a canonical spec to a job. Returns a new BoundJobSpec containing a
 * frozen snapshot of execution-critical fields.
 *
 * IMMUTABILITY: Once a Job has a `boundJobSpec`, it must not be replaced.
 * Use `canBindSpec` before calling.
 */
export function bindSpec(spec: JobSpec, now: Date = new Date()): BoundJobSpec {
  return {
    specId: spec.specId,
    versionNumber: spec.versionNumber,
    archetype: spec.archetype,
    boundAt: now.toISOString(),
    snapshot: {
      label: spec.label,
      archetype: spec.archetype,
      status: spec.status,
      // Deep-clone phases & BOM so future mutations to the registry object
      // (or to the snapshot) cannot leak into the other.
      phases: spec.phases.map(p => ({
        name: p.name,
        label: p.label,
        responsibleTeam: p.responsibleTeam,
        requiredPhotos: [...p.requiredPhotos],
        checklistItems: p.checklistItems.map(c => ({ ...c })),
      })),
      billOfMaterials: spec.billOfMaterials.map(b => ({ ...b })),
    },
  };
}

export function canBindSpec(existing?: BoundJobSpec): boolean {
  return !existing;
}

/** Format helper, e.g. "TTS-STD-60x32-v1" */
export function formatSpecRef(b: Pick<BoundJobSpec, "specId" | "versionNumber">): string {
  return `${b.specId}-v${b.versionNumber}`;
}

// ── Project-aware photo labels ────────────────────────────────────────────
// Required-photo identifiers are stable enums (used for execution / QA / BOM
// matching). For human-facing surfaces we resolve a friendly label that
// reflects the actual project — e.g. for a Tub-to-Shower job the "before"
// shot is described as "Existing tub", but for a Shower Remodel it would
// read "Existing shower". Pure presentation; does not change execution.

type ProjectKind = "tts" | "shower_remodel" | "full_bath" | "generic";

function classifyProject(projectType?: string): ProjectKind {
  const t = (projectType || "").toLowerCase();
  if (t.includes("tub to shower") || t.includes("tub-to-shower")) return "tts";
  if (t.includes("shower remodel") || t.includes("shower replacement")) return "shower_remodel";
  if (t.includes("full bath")) return "full_bath";
  return "generic";
}

const PHOTO_LABELS: Record<RequiredPhotoType, Partial<Record<ProjectKind, string>> & { default: string }> = {
  // Prep
  prep_floor_protection:  { default: "Floor protection laid" },
  prep_water_shutoff:     { default: "Water shut-off verified" },
  prep_workspace_staged:  { default: "Workspace staged" },
  // Demo / before — project-aware
  before_existing_tub: {
    default: "Before — existing fixture",
    tts: "Before — existing tub",
    shower_remodel: "Before — existing shower",
    full_bath: "Before — existing tub/shower",
  },
  before_walls:               { default: "Before — surrounding walls" },
  before_plumbing_access:     { default: "Before — plumbing access" },
  demo_complete_substrate:    { default: "Demo complete — substrate exposed" },
  demo_debris_removed:        { default: "Demo debris removed" },
  // Plumbing
  rough_plumbing_valve:       { default: "Rough plumbing — valve" },
  rough_plumbing_drain:       { default: "Rough plumbing — drain" },
  pressure_test:              { default: "Pressure test held" },
  // Install — project-aware where helpful
  pan_set: {
    default: "Pan / base set",
    tts: "Shower pan set",
    shower_remodel: "Replacement pan set",
  },
  wall_panels_installed:      { default: "Wall panels installed" },
  glass_door_installed: {
    default: "Door installed",
    tts: "Glass shower door installed",
    shower_remodel: "Glass shower door installed",
  },
  trim_complete:              { default: "Trim & accessories complete" },
  // QA / final
  final_full_shower: {
    default: "Final — full unit",
    tts: "Final — full walk-in shower",
    shower_remodel: "Final — full shower",
    full_bath: "Final — full bath",
  },
  final_caulk_lines:          { default: "Final — caulk lines" },
  final_drain_function:       { default: "Final — drain function" },
  site_cleaned:               { default: "Site cleaned" },
  // Final QA / Closeout
  qa_final_shower_overview: {
    default: "Final Full Shower Overview",
    tts: "Final — full walk-in shower",
    shower_remodel: "Final — full shower",
  },
  qa_final_pan_basin: {
    default: "Final Shower Pan/Basin",
    tts: "Final — shower pan",
    shower_remodel: "Final — pan/basin",
  },
  qa_final_fixtures_accessories: {
    default: "Final Fixtures & Accessories",
    tts: "Final — shower fixtures",
    shower_remodel: "Final — fixtures & accessories",
  },
  qa_final_seat_installation: {
    default: "Final Seat Installation",
    tts: "Final — fold-up seat",
    shower_remodel: "N/A for this project type",
  },
  qa_water_test_verification: { default: "Water Test Verification" },
  qa_site_cleaned_complete:   { default: "Site Cleaned & Complete" },
};

/** Friendly, project-aware label for a required photo. */
export function getPhotoLabel(
  photoType: RequiredPhotoType,
  projectType?: string,
): string {
  const entry = PHOTO_LABELS[photoType];
  if (!entry) return photoType.replace(/_/g, " ");
  const kind = classifyProject(projectType);
  return entry[kind] ?? entry.default;
}
